import { useState, useContext, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import useGenericModal from "components/GenericModal/GenericModal-logic";
import { ModalContext } from "contexts/Modal";
import { strNonEmpty } from "util/strings";

const emptyInputs = { name: "", content: "" };

const validateInputs = (inputs, showModal) => {
  const nameNonEmpty = strNonEmpty(inputs.name);
  const contentNonEmpty = strNonEmpty(inputs.content);
  if (!nameNonEmpty || !contentNonEmpty) {
    showModal(
      "Save Config",
      `${nameNonEmpty ? "" : "Must include name. "}${
        contentNonEmpty ? "" : "Must include JSON body. "
      }`
    );
    return false;
  }
  return true;
};

const useAccountAdminConfigs = (accountId) => {
  const { showModal } = useContext(ModalContext);
  const { modalOpen, setModalOpen } = useGenericModal();

  const [config, setConfigs] = useState({});
  const [inputs, setInputs] = useState(emptyInputs);

  // --- Auth readiness + duplicate-call guard ---
  const [ready, setReady] = useState(false);
  const listInFlight = useRef(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setConfigs({});
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const ensureAuthed = async () => {
    await Auth.currentAuthenticatedUser();
  };

  const getAccountConfigs = async (accountIdArg) => {
    if (!ready || !accountIdArg) return;
    if (listInFlight.current) return;
    listInFlight.current = true;

    try {
      await ensureAuthed();
      const fetchedConfigs = await API.get(
        "AccountGatewayApi",
        `/account/${accountIdArg}/config`
      );
      setConfigs(fetchedConfigs);
    } catch (err) {
      console.error("error fetching configs", err);
    } finally {
      listInFlight.current = false;
    }
  };

  const getConfigKey = async (accountIdArg, configKey) => {
    if (!ready) return null;
    try {
      await ensureAuthed();
      return await API.get(
        "AccountGatewayApi",
        `/account/${accountIdArg}/config/${configKey}/`
      );
    } catch (err) {
      console.error("error fetching config by key", err);
      return null;
    }
  };

  const handleConfigSave = async (event, accountIdArg, inInputs, setOpen) => {
    if (event) event.preventDefault();
    if (!ready) return;

    try {
      await ensureAuthed();

      // Validate the inputs
      if (!validateInputs(inInputs, showModal)) return;

      const parsedJson = JSON.parse(inInputs.content);

      await API.put(
        "AccountGatewayApi",
        `/account/${accountIdArg}/config/${inInputs.name}`,
        { body: parsedJson, withCredentials: true }
      );

      setOpen(false);
      showModal("Save Config", "Config successfully saved.");
      getAccountConfigs(accountIdArg); // refresh list after save
    } catch (err) {
      console.error("error saving config", err);
      setOpen(false);
      showModal("Save Config", "Error occurred while saving config.");
    }
  };

  const handleConfigDelete = async (accountIdArg, configKey) => {
    if (!ready) return;

    try {
      await ensureAuthed();

      await API.del(
        "AccountGatewayApi",
        `/account/${accountIdArg}/config/${configKey}`,
        { withCredentials: true }
      );

      setModalOpen(false);
      showModal("Remove Config", "Config successfully removed.");
      getAccountConfigs(accountIdArg); // refresh list
    } catch (err) {
      console.error("error emptying config", err);
      setModalOpen(false);
      showModal("Remove Config", "Error occurred while removing config.");
    }
  };

  // Only fetch once auth is ready
  useEffect(() => {
    if (ready && accountId) getAccountConfigs(accountId);
  }, [ready, accountId]);

  return {
    modalOpen,
    setModalOpen,
    inputs,
    setInputs,
    config,
    setConfigs,
    getAccountConfigs,
    handleConfigSave,
    handleConfigDelete,
    getConfigKey,
  };
};

export default useAccountAdminConfigs;
